import { BODEGA_EXTERIOR_STANDPOINT, SUBWAY_GATE, CORONA_EXIT_GATE, HIDDEN_STASH_MARKER, LAUNDROMAT_EXTERIOR_STANDPOINT, BASEMENT_HATCH, ROOFTOP_VANTAGE_MARKER, EL_STATION_VANTAGE_MARKER } from './layout';
import { INTERIOR_EXIT_MARKER, HEAL_STATION, CRAFT_BENCH, STASH_STANDPOINT, HUNT_BOARD, WARDROBE_STAND } from './interior';
import { LAUNDROMAT_EXIT_MARKER, LAUNDROMAT_STASH_MARKER } from './interiorLaundromat';
import { BASEMENT_UP_MARKER, BASEMENT_SEALED_DOOR_MARKER } from './interiorBasement';
import { LAIR_ENTRANCE_MARKER } from './denLair';
import { TRAP_HOLE_POS } from './denSewer';

export type InteractAction =
  | 'enter_bodega'
  | 'exit_bodega'
  | 'locked_seam'
  | 'heal'
  | 'stash'
  | 'craft'
  | 'hidden_stash'
  | 'hunt_board'
  | 'enter_laundromat'
  | 'exit_laundromat'
  | 'laundromat_stash'
  | 'enter_basement'
  | 'exit_basement'
  | 'rooftop_vantage'
  | 'wardrobe'
  | 'enter_lair'
  | 'enter_sewer'
  | 'el_platform_vantage';

export interface Interactable {
  id: string;
  label: string;
  x: number;
  z: number;
  radius: number;
  action: InteractAction;
  /** Shown by `handleInteract` (main.ts) instead of performing a teleport — the block-seam stub (PLAN-CITY §5) reads as a real, load-bearing door but goes nowhere yet. */
  lockedMessage?: string;
  /**
   * B-1 review finding (CONFIRMED, correctness lens): every OTHER
   * interactable's real usable position sits at (or very near) street/floor
   * level, so a pure XZ radius has always been enough — this game had no
   * interactable where a DIFFERENT height at the same XZ column meaningfully
   * changes whether the player can actually reach it, until the rooftop
   * vantage (a raised platform, world/layout.ts's ROOFTOP_VANTAGE_TOWER).
   * Optional and additive: omitted (every pre-B-1 interactable) means the
   * XZ radius alone decides, byte-identical to before this field existed.
   * When present, `InteractSystem.update()` also requires the player's own Y
   * within `tolerance` of `y` — this is a coarse "is this plausible at all"
   * filter for the ON-SCREEN PROMPT only (generous enough to stay lit
   * through a real jump's apex, not just while standing still), NOT the
   * anti-exploit gate itself; the actual "must be genuinely grounded on top"
   * check lives in main.ts's own 'rooftop_vantage' handler, since a height
   * check alone can't distinguish "standing on the platform" from "airborne
   * exactly this high mid-jump" — see that handler's own comment.
   */
  requireY?: { y: number; tolerance: number };
}

/**
 * S05's interact-prompt system (PLAN-CITY §5: "interact prompts"). Every
 * proximity trigger the player can walk up to and press the on-screen
 * INTERACT button near — the bodega's two-way door, and one block-seam stub
 * (§7's 2A route: Astoria→Corona→Flushing) at the subway gate layout.ts's
 * own SUBWAY_GATE comment already flagged as "S05 territory." Both the
 * bodega interactables and `world/checkpoints.ts`'s `bodega` checkpoint
 * trigger read from the SAME `BODEGA_EXTERIOR_STANDPOINT` (layout.ts) —
 * walking up to interact with the door and reaching the bodega checkpoint
 * happen at the same physical spot, by construction, not by coincidence.
 */
export const INTERACTABLES: Interactable[] = [
  {
    id: 'bodega_enter',
    label: 'ENTER BODEGA',
    x: BODEGA_EXTERIOR_STANDPOINT.x,
    z: BODEGA_EXTERIOR_STANDPOINT.z,
    radius: 2.2,
    action: 'enter_bodega',
  },
  {
    id: 'bodega_exit',
    label: 'EXIT BODEGA',
    x: INTERIOR_EXIT_MARKER.x,
    z: INTERIOR_EXIT_MARKER.z,
    radius: 1.6,
    action: 'exit_bodega',
  },
  {
    id: 'subway_seam',
    label: 'SUBWAY — LOCKED',
    // A standing point on the landing, 1.5m north of the gate (which sits
    // right at the landing's own south edge, z≈−17.9) — comfortably clear
    // of the south rim collider (layout.ts's allWalls(), minZ−0.4..minZ)
    // and matches world/checkpoints.ts's own `subway_landing` checkpoint
    // z (−16), the same "stand just outside the thing you're interacting
    // with" pattern BODEGA_EXTERIOR_STANDPOINT already establishes.
    x: SUBWAY_GATE.x,
    z: SUBWAY_GATE.z + 1.5,
    radius: 2.2,
    action: 'locked_seam',
    lockedMessage: '🔒 CORONA LINE — more of the city is coming',
  },
  {
    id: 'corona_exit_seam',
    label: 'CORONA AVE — LOCKED',
    // S10: the street-level Corona seam (distinct from subway_seam above —
    // C02 board 1 names them as two separate zones). Standing point 2m west
    // of the gate, on the near/reachable side (the far side beyond x=34.15
    // is still inside the play boundary at x=38, so this is deliberately the
    // WEST approach, matching the gate's own street-facing orientation).
    x: CORONA_EXIT_GATE.x - 2,
    z: CORONA_EXIT_GATE.z,
    radius: 2.4,
    action: 'locked_seam',
    lockedMessage: '🔒 CORONA AVE — the road continues, someday',
  },
  {
    id: 'hidden_stash',
    label: 'SEARCH THE STASH',
    x: HIDDEN_STASH_MARKER.x,
    z: HIDDEN_STASH_MARKER.z,
    radius: 1.8,
    action: 'hidden_stash',
  },
  {
    id: 'bodega_heal',
    label: 'REST — HEAL UP',
    x: HEAL_STATION.x,
    z: HEAL_STATION.z,
    radius: 1.4,
    action: 'heal',
  },
  {
    id: 'bodega_stash',
    label: 'CHECK THE STASH',
    x: STASH_STANDPOINT.x,
    z: STASH_STANDPOINT.z,
    // Review finding (CONFIRMED, don't-break-list lens): at the original
    // 1.6, this trigger circle genuinely overlapped bodega_exit (by 0.10m)
    // and both bodega_heal/bodega_craft (0.045m each) — a player standing
    // in one of those slivers would see the on-screen prompt flicker
    // between two unrelated labels tick to tick. 1.3 clears all three with
    // real margin (≥0.2m) while still comfortably covering a natural
    // standing position near the counter.
    radius: 1.3,
    action: 'stash',
  },
  {
    id: 'bodega_craft',
    label: 'CRAFTING BENCH',
    x: CRAFT_BENCH.x,
    z: CRAFT_BENCH.z,
    radius: 1.4,
    action: 'craft',
  },
  {
    id: 'bodega_huntboard',
    label: 'HUNT BOARD',
    x: HUNT_BOARD.x,
    z: HUNT_BOARD.z,
    radius: 1.0,
    action: 'hunt_board',
  },
  {
    id: 'bodega_wardrobe',
    label: 'WARDROBE',
    x: WARDROBE_STAND.x,
    z: WARDROBE_STAND.z,
    radius: 1.0,
    action: 'wardrobe',
  },
  // --- B-2: laundromat (world/interiorLaundromat.ts) ---
  {
    id: 'laundromat_enter',
    label: 'ENTER LAUNDROMAT',
    x: LAUNDROMAT_EXTERIOR_STANDPOINT.x,
    z: LAUNDROMAT_EXTERIOR_STANDPOINT.z,
    radius: 2.2,
    action: 'enter_laundromat',
  },
  {
    id: 'laundromat_exit',
    label: 'EXIT LAUNDROMAT',
    x: LAUNDROMAT_EXIT_MARKER.x,
    z: LAUNDROMAT_EXIT_MARKER.z,
    // 1.4, not bodega_exit's 1.6: this room's half-depth (2.5) is smaller
    // than the bodega's (3.0), and the two interior markers sit 2.6m apart
    // (z1.9 vs z-0.7) — 1.4+0.9 (laundromat_stash below) leaves a real 0.3m
    // margin between the circles, matching the ≥0.2m bar the S10 review
    // established for bodega_stash's own overlap fix.
    radius: 1.4,
    action: 'exit_laundromat',
  },
  {
    id: 'laundromat_stash',
    label: 'SEARCH THE MACHINES',
    x: LAUNDROMAT_STASH_MARKER.x,
    z: LAUNDROMAT_STASH_MARKER.z,
    radius: 0.9,
    action: 'laundromat_stash',
  },
  // --- B-2: basement vestibule (world/interiorBasement.ts) — button-only, see that file's own header comment ---
  {
    id: 'basement_hatch',
    label: 'DESCEND — LOOSE GRATE',
    x: BASEMENT_HATCH.x,
    z: BASEMENT_HATCH.z,
    radius: 2.0,
    action: 'enter_basement',
  },
  {
    id: 'basement_up',
    label: 'CLIMB BACK UP',
    x: BASEMENT_UP_MARKER.x,
    z: BASEMENT_UP_MARKER.z,
    radius: 1.4,
    action: 'exit_basement',
  },
  {
    id: 'basement_sealed_door',
    label: 'SEALED DOOR',
    x: BASEMENT_SEALED_DOOR_MARKER.x,
    z: BASEMENT_SEALED_DOOR_MARKER.z,
    radius: 1.4,
    action: 'locked_seam',
    lockedMessage: '🔒 Something is down there. Not open yet.',
  },
  // --- B-1: rooftop vantage (world/layout.ts's ROOFTOP_VANTAGE_TOWER) ---
  // Two review findings, both fixed together here. (1) The platform is a
  // full 2.4×2.4m square (half-width 1.2m) — a player can legitimately stand
  // anywhere on it, including a corner up to 1.2*sqrt(2)≈1.697m from center,
  // so the original radius (1.0) left a real dead zone near the edges where
  // a genuinely-standing player saw no prompt. 1.8 clears every corner with
  // real margin. (2) Widening the XZ radius alone would also light up the
  // prompt for a walking, non-jumping player near the tower's BASE (the
  // tower's own solid collider only keeps them half-width+PLAYER_RADIUS≈1.5m
  // away, less than the new 1.8 radius) — `requireY` closes that: the prompt
  // only shows when the player's own Y is within 0.6m of the tower's real
  // top (13.2m), which a grounded street-level player (y=12, ROOFTOP_D's own
  // height) fails by a full 0.6m margin, but a player mid-jump near the
  // apex (measured peak ~13.34m) or standing on top (13.2m exactly) passes.
  // This tolerance is deliberately generous for the PROMPT — it is NOT the
  // anti-exploit gate (a height check alone can't tell "standing on top"
  // from "airborne this high mid-jump"); the real grounded check lives in
  // main.ts's own 'rooftop_vantage' handler. No XZ-overlap risk with any
  // OTHER interactable: this is the only trigger whose real usable position
  // is off the ground plane, and its XZ column (x[15.2,18.8] z[-25.8,-22.2]
  // at this wider radius) sits inside ROOFTOP_D's own footprint, far from
  // every other interactable's zone.
  {
    id: 'rooftop_vantage',
    label: 'TAKE IN THE VIEW',
    x: ROOFTOP_VANTAGE_MARKER.x,
    z: ROOFTOP_VANTAGE_MARKER.z,
    radius: 1.8,
    action: 'rooftop_vantage',
    requireY: { y: ROOFTOP_VANTAGE_MARKER.y, tolerance: 0.6 },
  },
  // --- K-2/L5: the two TELEPORT-gated den entrances, converted from a walk-
  // through auto-teleport to a real interact prompt (labels hint, don't
  // spoil, per PLAN-CITY3 §4's own wording). ---
  {
    id: 'lair_entrance',
    label: 'SOMETHING CHEWED THROUGH HERE',
    x: LAIR_ENTRANCE_MARKER.x,
    z: LAIR_ENTRANCE_MARKER.z,
    radius: 1.1,
    action: 'enter_lair',
  },
  {
    id: 'sewer_entrance',
    label: 'DROP INTO THE SEWER',
    x: TRAP_HOLE_POS.x,
    z: TRAP_HOLE_POS.z,
    // Isolated (nearest other interactable/collider is the utility-tunnel
    // corridor's own boundary gap, a real 5.6m clear per denSewer.ts's own
    // TRAP_HOLE_POS comment) — no margin concerns here, radius picked to
    // roughly match the hole's own visual mouth (denSewer.ts's
    // TRAP_HOLE_MOUTH_RADIUS, 1.2).
    radius: 1.3,
    action: 'enter_sewer',
  },
  // --- E-3 (PLAN-CITY3 §6): the el platform's vantage-style interactable,
  // mirroring rooftop_vantage's own requireY shape exactly — REQUIRED here,
  // not optional: `corona_exit_seam` above (x=32, z=0, radius 2.4) sits at
  // STREET level in the same XZ neighborhood (this marker is x=29, z=-1.3,
  // real distance ~3.4m — well inside 2.4+radius without a height gate).
  // requireY excludes every ground-level player by a huge margin (deck
  // height 7.4 vs. street 0-1.4), so the two circles can never both be live
  // for the same player regardless of XZ proximity — no pairwise
  // prompt-circle conflict despite sitting near each other on the map.
  {
    id: 'el_platform_vantage',
    label: 'TAKE IN THE VIEW',
    x: EL_STATION_VANTAGE_MARKER.x,
    z: EL_STATION_VANTAGE_MARKER.z,
    radius: 1.6,
    action: 'el_platform_vantage',
    requireY: { y: EL_STATION_VANTAGE_MARKER.y, tolerance: 0.6 },
  },
];

/**
 * Nearest in-range interactable, or null. A generous but bounded scan
 * (15 entries as of B-2, 9 after S11, was 3 pre-S10) — no spatial index needed
 * at this count, same "cheap enough at this size to run per-query" call
 * BlockPhysics.segmentClear's own comment already makes for ~20 walls.
 */
export class InteractSystem {
  /**
   * `playerY` is optional — genuinely omittable, not just defaulted, so
   * every pre-B-1 call site (and harness/run.mjs's own diagnostic call,
   * which only ever probes XZ-only interactables) keeps working unchanged.
   * Any interactable with `requireY` set FAILS CLOSED when `playerY` is
   * omitted (never matches) rather than falling back to "assume it's fine"
   * — the same fails-closed discipline this project's own AIM_RANGE/NaN gate
   * already established (T2 review finding) for a missing/invalid value on
   * a gate that exists specifically to keep something from firing wrongly.
   */
  update(playerX: number, playerZ: number, playerY?: number): Interactable | null {
    let nearest: Interactable | null = null;
    let nearestDist = Infinity;
    for (const it of INTERACTABLES) {
      if (it.requireY && (playerY === undefined || Math.abs(playerY - it.requireY.y) > it.requireY.tolerance)) continue;
      const dist = Math.hypot(playerX - it.x, playerZ - it.z);
      if (dist <= it.radius && dist < nearestDist) {
        nearest = it;
        nearestDist = dist;
      }
    }
    return nearest;
  }
}
